// ShortcutSettingsPanel — ショートカット一覧 + リバインド + 永続化 + reset の
// **自己完結パネル部品** (shortcut-settings.md §4-§5、スライス2)。
//
// 設計意図 (二段構え): 今は独立「ショートカット」ボタン + 独立モーダルから開くが、
// 将来は 1 つの「設定」ボタン + タブ shell に **そのまま再マウント**する。よって本
// component は **モーダル外枠と疎結合**な self-contained 部品にする (= 外枠 chrome /
// Esc / backdrop は呼び出し側 `ShortcutSettingsModal` が持ち、本体は中身だけ描く)。
//
// データ源は registry (`SHORTCUT_REGISTRY` + `GESTURE_SHORTCUTS`)。override は
// `shortcutOverridesContext` の singleton 経由で読み書きし、配線フックと同 snapshot を
// 共有する (= リバインドが即座に効く)。
//
// 行の種別:
//   - **リバインド可能行** (`rebindable` action): 現 binding 表示 + キャプチャボタン +
//     per-row reset。キャプチャ中は次の keydown を chord 録音 (Esc キャンセル)。
//     **同じキーへの複数割当は許可**する (= CSP流 循環ショートカット、2026-08-21確定。
//     旧 v1 の steal-with-warn — 衝突相手を未割当化して移す確認ダイアログ — は撤去した。
//     「同じキーは 1 つだけ」というエラーだったものが「循環の宣言」という正の仕様に
//     反転したため)。代わりに同じキーを持つ他 action があれば行の下へ**情報提供の注記**
//     (「同じキーで循環」) を出す (`findSameKeyActionIds`)。
//   - **固定行** (guard 2 action + help.shortcuts + ジェスチャ): 🔒 アイコン + 説明のみ
//     (= リバインド不可、v1)。

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  resetAllShortcutOverrides,
  resetShortcutOverride,
  setShortcutOverride,
  useShortcutOverrides,
} from './layouts/shortcutOverridesContext.js';
import {
  findSameKeyActionIds,
  formatBindingsLabel,
  formatChordFromEvent,
  GESTURE_SHORTCUTS,
  resolveEffectiveKeys,
  SHORTCUT_REGISTRY,
  type ShortcutAction,
  type ShortcutLayout,
} from './layouts/shortcutRegistry.js';

export interface ShortcutSettingsPanelProps {
  /** 現在のレイアウト (= この layout に出ない action はグレーアウト表示する)。 */
  readonly currentLayout: ShortcutLayout;
}

/** registry を category 順にグルーピングする (= 宣言順を保ったまま category で束ねる)。 */
const groupByCategory = (
  actions: readonly ShortcutAction[],
): ReadonlyArray<readonly [string, readonly ShortcutAction[]]> => {
  const order: string[] = [];
  const map = new Map<string, ShortcutAction[]>();
  for (const a of actions) {
    if (!map.has(a.category)) {
      map.set(a.category, []);
      order.push(a.category);
    }
    map.get(a.category)?.push(a);
  }
  return order.map((cat) => [cat, map.get(cat) ?? []] as const);
};

export const ShortcutSettingsPanel = ({
  currentLayout,
}: ShortcutSettingsPanelProps): React.ReactNode => {
  const overrides = useShortcutOverrides();
  // キャプチャ中の action id (= 「キーを押してください」状態)。null なら待機なし。
  const [capturingId, setCapturingId] = useState<string | null>(null);

  const groups = useMemo(() => groupByCategory(SHORTCUT_REGISTRY), []);
  const labelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of SHORTCUT_REGISTRY) m.set(a.id, a.label);
    return m;
  }, []);

  // action ごとに「現在同じキーを共有している他 action のラベル」を求める (= CSP流
  // 循環ショートカットの案内表示、2026-08-21)。ブロックはしない、情報提供のみ。
  const sharedLabelsByAction = useMemo(() => {
    const map = new Map<string, readonly string[]>();
    for (const action of SHORTCUT_REGISTRY) {
      const ids = new Set<string>();
      for (const key of resolveEffectiveKeys(action, overrides)) {
        for (const otherId of findSameKeyActionIds(key, action.id, SHORTCUT_REGISTRY, overrides)) {
          ids.add(otherId);
        }
      }
      map.set(
        action.id,
        [...ids].map((id) => labelById.get(id) ?? id),
      );
    }
    return map;
  }, [overrides, labelById]);

  // 1 つの binding を targetId に割当てる。同じキーの他 action があっても**ブロック
  // しない** (= 循環の宣言として許可する、2026-08-21、旧 steal-with-warn は撤去)。
  const applyBinding = useCallback((targetId: string, candidate: string): void => {
    setShortcutOverride(targetId, [candidate]);
  }, []);

  // キャプチャ中の keydown を chord 録音する (Esc でキャンセル)。capturingId のときだけ。
  useEffect(() => {
    if (capturingId === null) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') {
        setCapturingId(null);
        return;
      }
      const chord = formatChordFromEvent({
        key: e.key,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        altKey: e.altKey,
        shiftKey: e.shiftKey,
      });
      if (chord === null) return; // 修飾キー単独 → 本キーを待つ。
      const id = capturingId;
      setCapturingId(null);
      applyBinding(id, chord);
    };
    // capture フェーズで掴んで配線フック (window/document) より先に食う。
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [capturingId, applyBinding]);

  return (
    <div className="text-[var(--color-text)]">
      <p className="mb-3 text-[11px] text-[var(--color-text-muted)]">
        この端末でのみ保存される個人設定です。キーを変更してもルームの他の参加者には影響しません。
      </p>
      {groups.map(([category, actions]) => (
        <section key={category} className="mb-4">
          <h3 className="mb-1 text-[11px] font-bold text-[var(--color-text-muted)]">{category}</h3>
          <ul className="flex flex-col gap-1">
            {actions.map((action) => (
              <ShortcutRow
                key={action.id}
                action={action}
                bindings={resolveEffectiveKeys(action, overrides)}
                inThisLayout={action.layouts.includes(currentLayout)}
                capturing={capturingId === action.id}
                isOverridden={action.id in overrides}
                sharedWith={sharedLabelsByAction.get(action.id) ?? []}
                onStartCapture={() => setCapturingId(action.id)}
                onCancelCapture={() => setCapturingId(null)}
                onReset={() => resetShortcutOverride(action.id)}
              />
            ))}
          </ul>
        </section>
      ))}

      {/* ジェスチャ系 (read-only「固定」行) — 一覧に説明だけ載せる (v1 リバインド対象外)。 */}
      <section className="mb-4">
        <h3 className="mb-1 text-[11px] font-bold text-[var(--color-text-muted)]">ジェスチャ</h3>
        <ul className="flex flex-col gap-1">
          {GESTURE_SHORTCUTS.map((g) => (
            <li
              key={g.id}
              className="flex items-center justify-between gap-2 rounded bg-[var(--color-bg-soft)] px-2 py-1 text-[12px]"
            >
              <span className="flex items-center gap-1 text-[var(--color-text-muted)]">
                <span aria-hidden="true">🔒</span>
                {g.label}
              </span>
              <span className="shrink-0 rounded bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[11px]">
                {g.keysLabel}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <div className="mt-4 flex justify-end border-white/10 border-t pt-3">
        <button
          type="button"
          onClick={resetAllShortcutOverrides}
          className="rounded border border-white/10 bg-[var(--color-bg-soft)] px-3 py-1 text-[11px] font-bold hover:bg-[var(--color-bg)]"
        >
          すべて既定に戻す
        </button>
      </div>
    </div>
  );
};

/**
 * 1 行 (= 1 action)。リバインド可能なら現 binding + キャプチャ + per-row reset、固定なら
 * 🔒 + binding 表示のみ。`inThisLayout=false` の action はグレーアウト (= 現 layout に
 * 出ない、shortcut-settings.md §2)。
 */
const ShortcutRow = ({
  action,
  bindings,
  inThisLayout,
  capturing,
  isOverridden,
  sharedWith,
  onStartCapture,
  onCancelCapture,
  onReset,
}: {
  readonly action: ShortcutAction;
  readonly bindings: readonly string[];
  readonly inThisLayout: boolean;
  readonly capturing: boolean;
  readonly isOverridden: boolean;
  /** 同じキーを共有している他 action のラベル一覧 (= 押すたびに循環する相手)。 */
  readonly sharedWith: readonly string[];
  readonly onStartCapture: () => void;
  readonly onCancelCapture: () => void;
  readonly onReset: () => void;
}): React.ReactNode => {
  const rebindable = action.rebindable === true;
  return (
    <li
      className={`flex flex-col gap-0.5 rounded px-2 py-1 text-[12px] ${
        inThisLayout ? 'bg-[var(--color-bg-soft)]' : 'bg-[var(--color-bg-soft)] opacity-40'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1">
          {!rebindable ? <span aria-hidden="true">🔒</span> : null}
          <span className="truncate">{action.label}</span>
          {!inThisLayout ? (
            <span className="shrink-0 text-[10px] text-[var(--color-text-muted)]">
              (このレイアウトでは無効)
            </span>
          ) : null}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <span className="rounded bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[11px]">
            {capturing ? 'キーを押してください…' : formatBindingsLabel(bindings)}
          </span>
          {rebindable ? (
            capturing ? (
              <button
                type="button"
                onClick={onCancelCapture}
                className="rounded border border-white/10 px-1.5 py-0.5 text-[11px] hover:bg-white/10"
              >
                中止
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onStartCapture}
                  title="キーを割り当てる"
                  className="rounded border border-white/10 bg-[var(--color-bg)] px-1.5 py-0.5 text-[11px] hover:bg-white/10"
                >
                  変更
                </button>
                <button
                  type="button"
                  onClick={onReset}
                  disabled={!isOverridden}
                  title="既定に戻す"
                  className="rounded border border-white/10 px-1.5 py-0.5 text-[11px] hover:bg-white/10 disabled:opacity-30"
                >
                  ↺
                </button>
              </>
            )
          ) : null}
        </span>
      </div>
      {/* CSP流 循環ショートカット (2026-08-21): 同じキーの他 action があれば案内する
          (= ブロックしない、押すたびに循環することの説明)。 */}
      {sharedWith.length > 0 ? (
        <p className="text-[10px] text-[var(--color-text-muted)]">
          同じキーで循環: {sharedWith.join(' / ')}
        </p>
      ) : null}
    </li>
  );
};
